<template>
  <div
    ref="container"
    class="sidebar-settings"
    :class="groupClass"
    :style="groupStyles"
  >
    <div
      v-if="title || $slots.header"
      class="sidebar-settings-title"
      :style="titleStyle"
      @click="onCollapse"
    >
      <slot
        name="header"
        :title="title"
        :collapsible="collapsible"
        :collapsed="collapsed"
      >
        <span>
          {{ title }}

          <span
            v-if="tooltip"
            :data-balloon="tooltipLabel"
            :data-balloon-length="tooltipStyle"
            :data-balloon-pos="tooltipPos"
            :class="['titleTooltip', tooltipClass]"
          >
            <slot
              name="tooltip"
              :icon="tooltipIcon"
            >
              <span :class="tooltipIcon" />
            </slot>
          </span>
        </span>
        <span
          v-if="collapsible"
          class="fas fa-chevron-right"
        />
      </slot>
    </div>

    <slot />
  </div>
</template>

<script>
const CollapsedHeight = 38;

export default {
  name: 'SidebarSettingsGroup',
  props: {
    collapsible: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      required: false,
      default: '',
    },
    titleStyle: {
      type: Object,
      default: () => ({}),
    },
    elementType: {
      type: String,
      default: 'general',
    },
    tooltip: {
      type: Boolean,
      default: false,
    },
    tooltipLabel: {
      type: String,
      default: '',
    },
    tooltipIcon: {
      type: String,
      default: 'fas fa-info-circle',
    },
    tooltipStyle: {
      type: String,
      default: 'info',
    },
    tooltipPos: {
      type: String,
      default: 'down',
    },
    tooltipClass: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      collapsed: true,
      height: 0,
      transitioning: false,
      timeout: null,
      ro: null,
    };
  },

  computed: {
    groupStyles() {
      if (!this.collapsible) {
      // Non-collapsible: let content size naturally
        return {};
      }

      return {
        maxHeight: `${this.collapsed ? CollapsedHeight : this.height}px`,
      };
    },

    groupClass() {
      return {
        'sidebar-settings--collapsible': this.collapsible,
        'sidebar-settings--collapsed': this.collapsed,
        'sidebar-settings--transitioning': this.transitioning,
      };
    },

    localStorageKey() {
      return `${this.elementType}_${this.title}_collapsed`
        .replace(/ /g, '_')
        .toLowerCase();
    },
  },

  mounted() {
    this.ro = new ResizeObserver(this.onResize);
    this.ro.observe(this.$refs.container);

    this.$nextTick(() => {
      if (!this.collapsible) {
      // Non-collapsible sections should always be expanded
        this.collapsed = false;
        return;
      }

      this.onResize();

      // Set initial state based on element type and section title
      const collapsed = window.localStorage.getItem(this.localStorageKey);
      this.collapsed = collapsed === 'true';
    });
  },

  beforeUnmount() {
    this.ro.disconnect();
    clearTimeout(this.timeout);
  },

  methods: {
    onResize() {
      // Scroll height will give us the full height even if it expands past the current bounds
      if (!this.transitioning && this.$refs.container) {
        const containerHeight = this.$refs.container.scrollHeight + 20;
        window.requestAnimationFrame(() => {
          this.height = containerHeight;
        });
      }
    },

    onCollapse() {
      if (this.collapsible) {
        this.transitioning = true;
        this.collapsed = !this.collapsed;
        window.localStorage.setItem(this.localStorageKey, this.collapsed);

        this.timeout = setTimeout(() => {
          this.transitioning = false;
        }, 200);
      }
    },
  },
};
</script>
